import React, { useEffect, useState, useCallback } from "react";
import { logsApi, Report, ReportData } from "../services/logsApi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  FileText,
  Plus,
  Trash2,
  RefreshCw,
  Calendar,
  Download,
  Eye,
  ArrowLeft,
  Database,
  AlertTriangle,
  AlertCircle,
  Clock,
  Server,
  TrendingUp,
  ChevronRight,
  X,
} from "lucide-react";
import CustomSelect from "./ui/CustomSelect";
import type { SelectOption } from "./ui/CustomSelect";
import Skeleton, {
  SkeletonCard,
  SkeletonChart,
  SkeletonTable,
} from "./ui/Skeleton";

const t = {
  primary: "#7c3aed",
  primaryMid: "#a78bfa",
  primaryLight: "#ede9fe",
  surface: "#ffffff",
  border: "#e9e5f5",
  danger: "#ef4444",
  dangerBg: "#fee2e2",
  warn: "#f59e0b",
  warnBg: "#fef3c7",
  success: "#22c55e",
  successBg: "#dcfce7",
  text: {
    primary: "#1e1b4b",
    secondary: "#6b7280",
    muted: "#9ca3b8",
    inverse: "#ffffff",
  },
};

const LEVEL_COLORS: Record<string, string> = {
  error: "#ef4444",
  warn: "#f59e0b",
  info: "#7c3aed",
  debug: "#94a3b8",
};
const PIE_COLORS = ["#7c3aed", "#ef4444", "#f59e0b", "#94a3b8", "#3b82f6"];

// ── Skeleton: Reports List ──
function ReportsListSkeleton() {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Skeleton width={140} height={26} className="mb-2" />
          <Skeleton width={220} height={14} />
        </div>
        <div className="flex gap-3">
          <Skeleton width={140} height={36} rounded="xl" />
          <Skeleton width={110} height={36} rounded="xl" />
        </div>
      </div>
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-2xl p-5"
            style={{
              backgroundColor: t.surface,
              border: `1px solid ${t.border}`,
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton width={40} height={40} rounded="xl" />
                <div>
                  <Skeleton width={200} height={16} className="mb-1" />
                  <Skeleton width={280} height={12} />
                </div>
              </div>
              <div className="flex gap-2">
                <Skeleton width={70} height={30} rounded="xl" />
                <Skeleton width={70} height={30} rounded="xl" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Skeleton: Report Detail ──
function ReportDetailSkeleton() {
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Skeleton width={36} height={36} rounded="xl" />
        <div>
          <Skeleton width={260} height={24} className="mb-1" />
          <Skeleton width={180} height={14} />
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
      <div className="grid grid-cols-12 gap-5 mb-5">
        <div className="col-span-12 lg:col-span-8">
          <SkeletonChart />
        </div>
        <div className="col-span-12 lg:col-span-4">
          <div
            className="rounded-2xl p-5"
            style={{
              backgroundColor: t.surface,
              border: `1px solid ${t.border}`,
            }}
          >
            <Skeleton width={120} height={18} className="mb-4" />
            <div className="flex justify-center py-4">
              <Skeleton width={160} height={160} rounded="full" />
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-6">
          <SkeletonTable rows={5} />
        </div>
        <div className="col-span-12 lg:col-span-6">
          <SkeletonTable rows={5} />
        </div>
      </div>
    </div>
  );
}

// ── Generate Modal ──
function GenerateModal({
  onClose,
  onGenerate,
  generating,
}: {
  onClose: () => void;
  onGenerate: (params: {
    title: string;
    description: string;
    period: string;
    startDate: string;
    endDate: string;
  }) => void;
  generating: boolean;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [period, setPeriod] = useState("7d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const periodOptions: SelectOption[] = [
    { value: "24h", label: "Dernières 24h" },
    { value: "7d", label: "7 derniers jours" },
    { value: "30d", label: "30 derniers jours" },
    { value: "custom", label: "Personnalisé" },
  ];

  const handleSubmit = () => {
    let startDate: string;
    let endDate = new Date().toISOString();

    if (period === "custom") {
      if (!customStart || !customEnd) return;
      startDate = new Date(customStart).toISOString();
      endDate = new Date(customEnd).toISOString();
    } else {
      const start = new Date();
      if (period === "24h") start.setDate(start.getDate() - 1);
      else if (period === "7d") start.setDate(start.getDate() - 7);
      else if (period === "30d") start.setDate(start.getDate() - 30);
      startDate = start.toISOString();
    }

    onGenerate({ title, description, period, startDate, endDate });
  };

  const inputStyle: React.CSSProperties = {
    border: `1px solid ${t.border}`,
    color: t.text.primary,
    backgroundColor: "#faf9fc",
    outline: "none",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 animate-fade-in">
      <div
        className="w-full max-w-lg rounded-2xl p-6 shadow-xl animate-fade-in-up"
        style={{ backgroundColor: t.surface }}
      >
        <div className="flex items-center justify-between mb-5">
          <h3
            className="text-[17px] font-bold"
            style={{ color: t.text.primary }}
          >
            Générer un Rapport
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            style={{ border: "none", background: "transparent" }}
          >
            <X className="w-5 h-5" style={{ color: t.text.muted }} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label
              className="block text-[12px] font-medium mb-1.5"
              style={{ color: t.text.secondary }}
            >
              Titre (optionnel)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Rapport hebdomadaire API"
              className="w-full px-3 py-2.5 rounded-xl text-[13px]"
              style={inputStyle}
            />
          </div>
          <div>
            <label
              className="block text-[12px] font-medium mb-1.5"
              style={{ color: t.text.secondary }}
            >
              Description (optionnel)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes ou contexte pour ce rapport..."
              rows={2}
              className="w-full px-3 py-2.5 rounded-xl text-[13px] resize-none"
              style={inputStyle}
            />
          </div>
          <div>
            <label
              className="block text-[12px] font-medium mb-1.5"
              style={{ color: t.text.secondary }}
            >
              Période
            </label>
            <CustomSelect
              value={period}
              onChange={setPeriod}
              options={periodOptions}
              icon={<Calendar className="w-3.5 h-3.5" />}
              className="w-full"
            />
          </div>

          {period === "custom" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  className="block text-[12px] font-medium mb-1.5"
                  style={{ color: t.text.secondary }}
                >
                  Début
                </label>
                <input
                  type="datetime-local"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl text-[12px]"
                  style={inputStyle}
                />
              </div>
              <div>
                <label
                  className="block text-[12px] font-medium mb-1.5"
                  style={{ color: t.text.secondary }}
                >
                  Fin
                </label>
                <input
                  type="datetime-local"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl text-[12px]"
                  style={inputStyle}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[13px] font-medium transition-colors"
            style={{
              backgroundColor: t.primaryLight,
              color: t.primary,
              border: "none",
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              generating ||
              (period === "custom" && (!customStart || !customEnd))
            }
            className="px-5 py-2 rounded-lg text-[13px] font-medium text-white flex items-center gap-2 transition-all disabled:opacity-40"
            style={{ backgroundColor: t.primary, border: "none" }}
          >
            {generating ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {generating ? "Génération..." : "Générer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Report Card (list item) ──
function ReportCard({
  report,
  onView,
  onDelete,
}: {
  report: Report;
  onView: () => void;
  onDelete: () => void;
}) {
  const periodLabels: Record<string, string> = {
    "24h": "24 heures",
    "7d": "7 jours",
    "30d": "30 jours",
    custom: "Personnalisé",
  };

  return (
    <div
      className="rounded-2xl p-5 transition-all duration-300 hover:shadow-md group"
      style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: t.primaryLight }}
          >
            <FileText className="w-5 h-5" style={{ color: t.primary }} />
          </div>
          <div className="min-w-0 flex-1">
            <p
              className="text-[14px] font-bold truncate"
              style={{ color: t.text.primary }}
            >
              {report.title}
            </p>
            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
              <span className="text-[11px]" style={{ color: t.text.muted }}>
                {new Date(report.createdAt).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              <span
                className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: t.primaryLight, color: t.primary }}
              >
                {periodLabels[report.period] || report.period}
              </span>
              <span
                className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: t.successBg, color: t.success }}
              >
                {report.data?.summary?.total?.toLocaleString() || 0} appels
              </span>
              <span
                className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: t.dangerBg, color: t.danger }}
              >
                {report.data?.summary?.errors || 0} erreurs
              </span>
            </div>
            {report.description && (
              <p
                className="text-[11px] mt-1 truncate"
                style={{ color: t.text.muted }}
              >
                {report.description}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 ml-3 flex-shrink-0">
          <button
            onClick={onView}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all hover:shadow-sm"
            style={{
              backgroundColor: t.primaryLight,
              color: t.primary,
              border: "none",
            }}
          >
            <Eye className="w-3.5 h-3.5" /> Voir
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-red-50"
            style={{ border: "none", background: "transparent" }}
          >
            <Trash2 className="w-4 h-4" style={{ color: t.danger }} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════
// ── Main Component ──
// ══════════════════════════════════════════
export default function Reports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Detail view
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await logsApi.getReports();
      if (res.success) setReports(res.data);
    } catch (err) {
      console.error("Failed to load reports:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleGenerate = async (params: {
    title: string;
    description: string;
    period: string;
    startDate: string;
    endDate: string;
  }) => {
    setGenerating(true);
    try {
      const res = await logsApi.generateReport(params);
      if (res.success) {
        setReports((prev) => [res.data, ...prev]);
        setShowModal(false);
      }
    } catch (err) {
      console.error("Failed to generate report:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Supprimer ce rapport ?")) return;
    try {
      await logsApi.deleteReport(id);
      setReports((prev) => prev.filter((r) => r.id !== id));
      if (selectedReport?.id === id) setSelectedReport(null);
    } catch (err) {
      console.error("Failed to delete report:", err);
    }
  };

  const openReport = async (report: Report) => {
    if (report.data) {
      setSelectedReport(report);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await logsApi.getReport(report.id);
      if (res.success) setSelectedReport(res.data);
    } catch (err) {
      console.error("Failed to load report:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExport = (report: Report) => {
    const blob = new Blob([JSON.stringify(report.data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rapport-${report.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── LIST VIEW ──
  if (!selectedReport && !detailLoading) {
    if (loading) return <ReportsListSkeleton />;

    return (
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2
              className="text-[22px] font-bold"
              style={{ color: t.text.primary }}
            >
              Rapports
            </h2>
            <p
              className="text-[13px] mt-0.5"
              style={{ color: t.text.secondary }}
            >
              {reports.length} rapport{reports.length > 1 ? "s" : ""} généré
              {reports.length > 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium text-white transition-all"
              style={{ backgroundColor: t.primary, border: "none" }}
            >
              <Plus className="w-4 h-4" /> Nouveau rapport
            </button>
            <button
              onClick={loadReports}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all"
              style={{
                backgroundColor: t.primaryLight,
                color: t.primary,
                border: "none",
              }}
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </div>

        {reports.length > 0 ? (
          <div className="space-y-3 stagger-children">
            {reports.map((report) => (
              <ReportCard
                key={report.id}
                report={report}
                onView={() => openReport(report)}
                onDelete={() => handleDelete(report.id)}
              />
            ))}
          </div>
        ) : (
          <div
            className="rounded-2xl p-12 text-center"
            style={{
              backgroundColor: t.surface,
              border: `1px solid ${t.border}`,
            }}
          >
            <FileText
              className="w-12 h-12 mx-auto mb-3"
              style={{ color: t.text.muted }}
            />
            <p
              className="text-[15px] font-medium mb-1"
              style={{ color: t.text.primary }}
            >
              Aucun rapport
            </p>
            <p className="text-[13px] mb-4" style={{ color: t.text.secondary }}>
              Générez votre premier rapport pour analyser l'activité API
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-medium text-white"
              style={{ backgroundColor: t.primary, border: "none" }}
            >
              <Plus className="w-4 h-4" /> Générer un rapport
            </button>
          </div>
        )}

        {showModal && (
          <GenerateModal
            onClose={() => setShowModal(false)}
            onGenerate={handleGenerate}
            generating={generating}
          />
        )}
      </div>
    );
  }

  // ── DETAIL VIEW ──
  if (detailLoading) return <ReportDetailSkeleton />;
  if (!selectedReport) return null;

  const { data } = selectedReport;
  const summary = data?.summary;

  const dailyChart =
    data?.daily?.map((d) => ({
      date: d.date?.split("-").slice(1).join("/"),
      total: d.total,
      error: d.levels?.error || 0,
      warn: d.levels?.warn || 0,
      info: d.levels?.info || 0,
    })) || [];

  const levelPie = data?.levels
    ? Object.entries(data.levels).map(([key, value]) => ({
        name: key.charAt(0).toUpperCase() + key.slice(1),
        value,
        color: LEVEL_COLORS[key] || "#94a3b8",
      }))
    : [];

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSelectedReport(null)}
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors hover:bg-gray-100"
            style={{ border: `1px solid ${t.border}`, background: t.surface }}
          >
            <ArrowLeft
              className="w-4 h-4"
              style={{ color: t.text.secondary }}
            />
          </button>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: t.primaryLight }}
          >
            <FileText className="w-5 h-5" style={{ color: t.primary }} />
          </div>
          <div>
            <h2
              className="text-[20px] font-bold"
              style={{ color: t.text.primary }}
            >
              {selectedReport.title}
            </h2>
            <p className="text-[12px]" style={{ color: t.text.muted }}>
              Créé le{" "}
              {new Date(selectedReport.createdAt).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
              {" · "}par {selectedReport.author}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport(selectedReport)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-all"
            style={{
              backgroundColor: t.primaryLight,
              color: t.primary,
              border: "none",
            }}
          >
            <Download className="w-3.5 h-3.5" /> Exporter JSON
          </button>
          <button
            onClick={() => handleDelete(selectedReport.id)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-all hover:bg-red-50"
            style={{
              backgroundColor: t.dangerBg,
              color: t.danger,
              border: "none",
            }}
          >
            <Trash2 className="w-3.5 h-3.5" /> Supprimer
          </button>
        </div>
      </div>

      {selectedReport.description && (
        <p
          className="text-[13px] mb-5 p-3 rounded-xl"
          style={{
            backgroundColor: "#faf9fc",
            color: t.text.secondary,
            border: `1px solid ${t.border}`,
          }}
        >
          {selectedReport.description}
        </p>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger-children">
        <div
          className="rounded-2xl p-5 transition-all duration-300 hover:shadow-md"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-4 h-4" style={{ color: t.primary }} />
            <span
              className="text-[11px] font-medium uppercase tracking-wide"
              style={{ color: t.text.muted }}
            >
              Total Appels
            </span>
          </div>
          <p
            className="text-[22px] font-bold"
            style={{ color: t.text.primary }}
          >
            {summary?.total?.toLocaleString() || 0}
          </p>
        </div>
        <div
          className="rounded-2xl p-5 transition-all duration-300 hover:shadow-md"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" style={{ color: t.danger }} />
            <span
              className="text-[11px] font-medium uppercase tracking-wide"
              style={{ color: t.text.muted }}
            >
              Erreurs
            </span>
          </div>
          <p className="text-[22px] font-bold" style={{ color: t.danger }}>
            {summary?.errors?.toLocaleString() || 0}
          </p>
          <span className="text-[11px]" style={{ color: t.text.muted }}>
            {summary?.errorRate}% du total
          </span>
        </div>
        <div
          className="rounded-2xl p-5 transition-all duration-300 hover:shadow-md"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4" style={{ color: t.warn }} />
            <span
              className="text-[11px] font-medium uppercase tracking-wide"
              style={{ color: t.text.muted }}
            >
              Avertissements
            </span>
          </div>
          <p className="text-[22px] font-bold" style={{ color: t.warn }}>
            {summary?.warnings?.toLocaleString() || 0}
          </p>
        </div>
        <div
          className="rounded-2xl p-5 transition-all duration-300 hover:shadow-md"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Server className="w-4 h-4" style={{ color: t.primary }} />
            <span
              className="text-[11px] font-medium uppercase tracking-wide"
              style={{ color: t.text.muted }}
            >
              Services
            </span>
          </div>
          <p
            className="text-[22px] font-bold"
            style={{ color: t.text.primary }}
          >
            {summary?.serviceCount || 0}
          </p>
          {summary?.avgResponseTime != null && (
            <span className="text-[11px]" style={{ color: t.text.muted }}>
              ~{summary.avgResponseTime}ms moy.
            </span>
          )}
        </div>
      </div>

      {/* Row: Daily Activity + Level Donut */}
      <div className="grid grid-cols-12 gap-5 mb-5">
        <div
          className="col-span-12 lg:col-span-8 rounded-2xl p-5 animate-fade-in-up"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <h3
            className="text-[15px] font-bold mb-1"
            style={{ color: t.text.primary }}
          >
            Activité Quotidienne
          </h3>
          <p className="text-[11px] mb-4" style={{ color: t.text.secondary }}>
            Volume d'appels par jour sur la période
          </p>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyChart}>
                <defs>
                  <linearGradient id="rGradInfo" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={LEVEL_COLORS.info}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor={LEVEL_COLORS.info}
                      stopOpacity={0}
                    />
                  </linearGradient>
                  <linearGradient id="rGradError" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={LEVEL_COLORS.error}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor={LEVEL_COLORS.error}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f3f0ff"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  fontSize={11}
                  stroke={t.text.muted}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  fontSize={11}
                  stroke={t.text.muted}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: t.surface,
                    border: `1px solid ${t.border}`,
                    borderRadius: "12px",
                    fontSize: "12px",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="info"
                  stroke={LEVEL_COLORS.info}
                  fill="url(#rGradInfo)"
                  strokeWidth={2}
                  name="Info"
                />
                <Area
                  type="monotone"
                  dataKey="error"
                  stroke={LEVEL_COLORS.error}
                  fill="url(#rGradError)"
                  strokeWidth={2}
                  name="Erreurs"
                />
                <Area
                  type="monotone"
                  dataKey="warn"
                  stroke={LEVEL_COLORS.warn}
                  fill="transparent"
                  strokeWidth={2}
                  strokeDasharray="4 2"
                  name="Warn"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Level Donut */}
        <div
          className="col-span-12 lg:col-span-4 rounded-2xl p-5 animate-fade-in-up"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
            animationDelay: "80ms",
          }}
        >
          <h3
            className="text-[15px] font-bold mb-1"
            style={{ color: t.text.primary }}
          >
            Par Niveau
          </h3>
          <p className="text-[11px] mb-3" style={{ color: t.text.secondary }}>
            Répartition des logs
          </p>
          <div className="h-[170px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={levelPie}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={72}
                  dataKey="value"
                  paddingAngle={3}
                  strokeWidth={0}
                >
                  {levelPie.map((e, i) => (
                    <Cell key={i} fill={e.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: t.surface,
                    border: `1px solid ${t.border}`,
                    borderRadius: "12px",
                    fontSize: "12px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <p
                  className="text-[18px] font-bold"
                  style={{ color: t.text.primary }}
                >
                  {summary?.total?.toLocaleString() || 0}
                </p>
                <p className="text-[10px]" style={{ color: t.text.muted }}>
                  total
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-2">
            {levelPie.map((e, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: e.color }}
                  />
                  <span
                    className="text-[11px]"
                    style={{ color: t.text.secondary }}
                  >
                    {e.name}
                  </span>
                </div>
                <span
                  className="text-[11px] font-semibold"
                  style={{ color: t.text.primary }}
                >
                  {e.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row: Services Table + Top Errors + Top Endpoints */}
      <div className="grid grid-cols-12 gap-5 mb-5">
        {/* Services Performance */}
        <div
          className="col-span-12 lg:col-span-6 rounded-2xl p-5 animate-fade-in-up"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
            animationDelay: "120ms",
          }}
        >
          <h3
            className="text-[15px] font-bold mb-1"
            style={{ color: t.text.primary }}
          >
            Performance par Service
          </h3>
          <p className="text-[11px] mb-3" style={{ color: t.text.secondary }}>
            {data?.services?.length || 0} services analysés
          </p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                  <th
                    className="text-left py-2 px-2 text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Service
                  </th>
                  <th
                    className="text-right py-2 px-2 text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Appels
                  </th>
                  <th
                    className="text-right py-2 px-2 text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Erreurs
                  </th>
                  <th
                    className="text-right py-2 px-2 text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Taux err.
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.services?.map((svc, i) => (
                  <tr
                    key={i}
                    className="hover:bg-[#faf8ff] transition-colors"
                    style={{ borderBottom: `1px solid ${t.border}` }}
                  >
                    <td
                      className="py-2 px-2 text-[12px] font-medium"
                      style={{ color: t.text.primary }}
                    >
                      {svc.name}
                    </td>
                    <td
                      className="py-2 px-2 text-[12px] text-right"
                      style={{ color: t.text.secondary }}
                    >
                      {svc.total.toLocaleString()}
                    </td>
                    <td
                      className="py-2 px-2 text-[12px] text-right font-semibold"
                      style={{ color: t.danger }}
                    >
                      {svc.errors}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <span
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor:
                            parseFloat(svc.errorRate) > 10
                              ? t.dangerBg
                              : parseFloat(svc.errorRate) > 5
                                ? t.warnBg
                                : t.successBg,
                          color:
                            parseFloat(svc.errorRate) > 10
                              ? t.danger
                              : parseFloat(svc.errorRate) > 5
                                ? t.warn
                                : t.success,
                        }}
                      >
                        {svc.errorRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Errors + Top Endpoints */}
        <div className="col-span-12 lg:col-span-6 space-y-5">
          {/* Top Errors */}
          <div
            className="rounded-2xl p-5 animate-fade-in-up"
            style={{
              backgroundColor: t.surface,
              border: `1px solid ${t.border}`,
              animationDelay: "160ms",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4" style={{ color: t.danger }} />
              <h3
                className="text-[15px] font-bold"
                style={{ color: t.text.primary }}
              >
                Top Erreurs
              </h3>
            </div>
            {data?.topErrors?.length ? (
              <div className="space-y-2">
                {data.topErrors.map((err, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-[#faf8ff] transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span
                        className="text-[10px] font-bold w-5 text-center"
                        style={{ color: t.text.muted }}
                      >
                        {i + 1}
                      </span>
                      <span
                        className="text-[12px] truncate"
                        style={{ color: t.text.primary }}
                      >
                        {err.message}
                      </span>
                    </div>
                    <span
                      className="text-[11px] font-semibold flex-shrink-0 ml-2"
                      style={{ color: t.danger }}
                    >
                      {err.count}×
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p
                className="py-4 text-center text-[12px]"
                style={{ color: t.text.muted }}
              >
                Aucune erreur
              </p>
            )}
          </div>

          {/* Top Endpoints */}
          <div
            className="rounded-2xl p-5 animate-fade-in-up"
            style={{
              backgroundColor: t.surface,
              border: `1px solid ${t.border}`,
              animationDelay: "200ms",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4" style={{ color: t.primary }} />
              <h3
                className="text-[15px] font-bold"
                style={{ color: t.text.primary }}
              >
                Top Endpoints
              </h3>
            </div>
            {data?.topEndpoints?.length ? (
              <div className="space-y-2">
                {data.topEndpoints.map((ep, i) => {
                  const maxCount = data.topEndpoints[0]?.count || 1;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span
                        className="text-[10px] font-bold w-5 text-center"
                        style={{ color: t.text.muted }}
                      >
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span
                            className="text-[12px] font-medium truncate"
                            style={{ color: t.text.primary }}
                          >
                            {ep.path}
                          </span>
                          <span
                            className="text-[11px] font-semibold flex-shrink-0 ml-2"
                            style={{ color: t.primary }}
                          >
                            {ep.count}
                          </span>
                        </div>
                        <div
                          className="h-1 rounded-full overflow-hidden"
                          style={{ backgroundColor: "#f3f0ff" }}
                        >
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${(ep.count / maxCount) * 100}%`,
                              backgroundColor: t.primary,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p
                className="py-4 text-center text-[12px]"
                style={{ color: t.text.muted }}
              >
                Aucun endpoint
              </p>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <GenerateModal
          onClose={() => setShowModal(false)}
          onGenerate={handleGenerate}
          generating={generating}
        />
      )}
    </div>
  );
}
